import { Component, OnDestroy, OnInit, inject, input } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest, map, takeUntil } from 'rxjs';

import { SiteState } from 'src/app/shared/models';

import { AsyncPipe } from '@angular/common';
// eslint-disable-next-line max-len
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { TranslatePipe } from '@next/core-lib/i18n';
import { LoadingCardComponent } from '../../../shared/components/loading-card.component/loading-card.component';
import { VarietyPropertiesTGADto } from '../../shared/models/models';
import { selectVarietyProperties } from '../../store/tga.feature';
import { AdviceCardComponent } from '../advice-card/advice-card.component';
import { RiskLegendComponent } from '../risk-legend/risk-legend.component';

interface VarietyProperty {
  name: string;
  valueGroups?: string[][];
  isIncreasedRisk?: boolean;
}

interface VarietyPropertiesDisplay {
  cropVariety: string;
  properties: VarietyProperty[];
}

const OUTCOME_TRANSLATION_KEY = 'TGA.Advice.General.CropVarietyProperties.Outcome';
const PROPERTIES_TRANSLATION_KEY = 'TGA.Advice.General.CropVarietyProperties.Properties';

@Component({
  selector: 'app-variety-properties-risk-dashboard-widget',
  templateUrl: './variety-properties-risk-dashboard-widget.component.html',
  styleUrls: ['./variety-properties-risk-dashboard-widget.component.scss'],
  imports: [
    AdviceCardComponent,
    RiskLegendComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    LoadingCardComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})

export class VarietyPropertiesRiskDashboardWidgetComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);

  cropVarietyName = input.required<string>();

  varietyProperties$: Observable<VarietyPropertiesDisplay> = new Observable();
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.varietyProperties$ = combineLatest([
      this.store.select(selectVarietyProperties),
    ]).pipe(
      takeUntil(this.destroy$),
      map(([varietyData]) => {
        if (varietyData && 'data' in varietyData && varietyData.data) {
          return {
            cropVariety: this.cropVarietyName(),
            properties: this.transformVarietyData(varietyData.data),
          };
        }
        return {
          cropVariety: this.cropVarietyName(),
          properties: [] as VarietyProperty[],
        };
      }),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private transformVarietyData(data: VarietyPropertiesTGADto): VarietyProperty[] {
    return [
      // Aardappelmoeheid Globodera Ros 1/4 (A)
      this.createGloboderaProperty(
        `${PROPERTIES_TRANSLATION_KEY}.GloboderaRos14Score`,
        data.nvwaGloboderaRos14Score,
        data.nvwaGloboderaRos14Rv,
        data.tradingCompany,
        data.tradingGloboderaRos14Score,
      ),

      // Aardappelmoeheid Globodera Ros 2/3 (B/C)
      this.createGloboderaProperty(
        `${PROPERTIES_TRANSLATION_KEY}.GloboderaRos23Score`,
        data.nvwaGloboderaRos23Score,
        data.nvwaGloboderaRos23Rv,
        data.tradingCompany,
        data.tradingGloboderaRos23Score,
      ),

      // Aardappelmoeheid Globodera Pal 2 (D)
      this.createGloboderaProperty(
        `${PROPERTIES_TRANSLATION_KEY}.GloboderaPal2Score`,
        data.nvwaGloboderaPal2Score,
        data.nvwaGloboderaPal2Rv,
        data.tradingCompany,
        data.tradingGloboderaPal2Score,
      ),

      // Aardappelmoeheid Globodera Pal 3 (E)
      this.createGloboderaProperty(
        `${PROPERTIES_TRANSLATION_KEY}.GloboderaPal3Score`,
        data.nvwaGloboderaPal3Score,
        data.nvwaGloboderaPal3Rv,
        data.tradingCompany,
        data.tradingGloboderaPal3Score,
      ),

      // Aardappelmoeheid Globodera Ros 5
      this.createGloboderaProperty(
        `${PROPERTIES_TRANSLATION_KEY}.GloboderaRos5Score`,
        data.nvwaGloboderaRos5Score,
        data.nvwaGloboderaRos5Rv,
        data.tradingCompany,
        data.tradingGloboderaRos5Score,
      ),

      // Alternaria
      this.createSimpleRiskProperty(
        `${PROPERTIES_TRANSLATION_KEY}.ScoreAlternaria`,
        data.scoreAlternaria,
      ),

      // Fusarium
      this.createSimpleRiskProperty(
        `${PROPERTIES_TRANSLATION_KEY}.ScoreFusarium`,
        data.scoreFusarium,
      ),

      // Phytophthora knol
      this.createSimpleRiskProperty(
        `${PROPERTIES_TRANSLATION_KEY}.ScorePhytophthoraTuber`,
        data.scorePhytophthoraTuber,
      ),

      // Phytophthora loof
      this.createSimpleRiskProperty(
        `${PROPERTIES_TRANSLATION_KEY}.RasScorePhytophthora`,
        data.rasScorePhytophthora,
      ),

      // Schurft / gewone schurft
      this.createSimpleRiskProperty(
        `${PROPERTIES_TRANSLATION_KEY}.RasScoreSchurft`,
        data.rasScoreSchurft,
      ),

      // Virus - Yntn (knol)
      this.createSimpleRiskProperty(
        `${PROPERTIES_TRANSLATION_KEY}.ScoreVirusYntnTuber`,
        data.scoreVirusYntnTuber,
      ),

      // Virus - Bladrol
      this.createSimpleRiskProperty(
        `${PROPERTIES_TRANSLATION_KEY}.ScoreLeafrollVirus`,
        data.scoreLeafrollVirus,
      ),

      // Wratziekte F1
      this.createSimpleRiskProperty(
        `${PROPERTIES_TRANSLATION_KEY}.WartDiseaseF1Score`,
        data.nvwaWartDiseaseF1Score,
      ),

      // Wratziekte F2/6
      this.createSimpleRiskProperty(
        `${PROPERTIES_TRANSLATION_KEY}.WartDiseaseF26Score`,
        data.nvwaWartDiseaseF26Score,
      ),

      // Wratziekte F18
      this.createSimpleRiskProperty(
        `${PROPERTIES_TRANSLATION_KEY}.WartDiseaseF18Score`,
        data.nvwaWartDiseaseF18Score,
      ),

      // Zwarte spikkel
      this.createSimpleRiskProperty(
        `${PROPERTIES_TRANSLATION_KEY}.RasScoreZwarteSpikkel`,
        data.rasScoreZwarteSpikkel,
      ),

      // Knolzetting (different value ranges)
      this.createKnolzettingProperty(data.knolZetting),

      // Vroegheid (just display value or -)
      {
        name: `${PROPERTIES_TRANSLATION_KEY}.VroegheidRas`,
        valueGroups: [[data.vroegheidRas !== null && data.vroegheidRas !== undefined ? data.vroegheidRas.toString() : '-']],
        isIncreasedRisk: false,
      },
    ];
  }

  private createGloboderaProperty(
    name: string,
    nvwaScore: number | null | undefined,
    rvValue: number | null | undefined,
    tradingCompany: string | null | undefined,
    tradingScore: number | null | undefined,
  ): VarietyProperty {
    const valueGroups: string[][] = [];

    // Group 1: NVWA: score (or - if empty)
    valueGroups.push([nvwaScore !== null && nvwaScore !== undefined ? `NVWA: ${nvwaScore}` : 'NVWA: -']);

    // Group 2: RV: value (≤ 1 if 1, or value, or - if empty)
    let rvDisplay = '-';
    if (rvValue !== null && rvValue !== undefined) {
      if (rvValue >= 0 && rvValue <= 1) {
        rvDisplay = '\u2266 1';
      } else {
        rvDisplay = rvValue.toString();
      }
    }
    valueGroups.push([`RV: ${rvDisplay}`]);

    // Group 3: Trading company + translated score + / + trading score
    if (tradingCompany && tradingScore !== null && tradingScore !== undefined) {
      valueGroups.push([`${tradingCompany}:`, this.translateScore(tradingScore), '/', `${tradingScore}`]);
    } else {
      valueGroups.push(['- / -']);
    }

    return {
      name,
      valueGroups,
      isIncreasedRisk: nvwaScore !== null && nvwaScore !== undefined && nvwaScore < 5.5,
    };
  }

  private createSimpleRiskProperty(name: string, score: number | null | undefined): VarietyProperty {
    const values: string[] = [];
    if (score !== null && score !== undefined) {
      values.push(`${score} - `, this.translateScore(score));
    } else {
      values.push('-');
    }
    return {
      name,
      valueGroups: [values],
      isIncreasedRisk: score !== null && score !== undefined && score < 5.5,
    };
  }

  private createKnolzettingProperty(score: number | null | undefined): VarietyProperty {
    const values: string[] = [];
    if (score !== null && score !== undefined) {
      values.push(`${score} - `, this.translateKnolzettingScore(score));
    } else {
      values.push('-');
    }
    return {
      name: `${PROPERTIES_TRANSLATION_KEY}.KnolZetting`,
      valueGroups: [values],
      isIncreasedRisk: score !== null && score !== undefined && score < 5.5,
    };
  }

  private translateScore(score: number | null | undefined): string {
    if (score === null || score === undefined) return '-';
    if (score <= 4) return `${OUTCOME_TRANSLATION_KEY}.HighlySusceptible`;
    if (score <= 6) return `${OUTCOME_TRANSLATION_KEY}.Susceptible`;
    if (score <= 8) return `${OUTCOME_TRANSLATION_KEY}.ModeratelySusceptible`;
    if (score <= 9) return `${OUTCOME_TRANSLATION_KEY}.ModeratelyToNotSusceptible`;
    if (score <= 10) return `${OUTCOME_TRANSLATION_KEY}.NotSusceptible`;
    return '-';
  }

  private translateKnolzettingScore(score: number | null | undefined): string {
    if (score === null || score === undefined) return '-';
    if (score <= 4) return `${OUTCOME_TRANSLATION_KEY}.VeryLow`;
    if (score <= 6) return `${OUTCOME_TRANSLATION_KEY}.QuiteLow`;
    if (score <= 7) return `${OUTCOME_TRANSLATION_KEY}.Moderate`;
    if (score <= 8) return `${OUTCOME_TRANSLATION_KEY}.QuiteHigh`;
    if (score <= 9) return `${OUTCOME_TRANSLATION_KEY}.High`;
    if (score <= 10) return `${OUTCOME_TRANSLATION_KEY}.VeryHigh`;
    return '-';
  }
}

